import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';


import {AccountLink} from 'src/components/links';
import {RequiredMark} from 'src/components/required-mark';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {ImportSettings} from 'src/modules/profile/account/create-user/steps/users-settings/components/imports-settings';
import {ReplaceConfirmMessage, isDisabled} from 'src/modules/profile/user/components/replace-search-settings';
import {getAccountName} from 'src/openapi/admin/apis/GetAccountNameApi';
import {useAppDispatch} from 'src/store/hooks';

import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import {
    selectBulkUserImportImportSettings,
    selectBulkUserImportTargetAccount,
    setImportSettings,
    setTargetAccount,
} from '../../services/add-attendees-bulk.slice';

import {UploadBulkUserImportFileBlock} from './user-import-file-upload/upload-bulk-user-import-file';

type Props = {
    accountId: number,
};

export const UploadFileStep: React.FC<Props> = ({accountId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const targetAccount = useSelector(selectBulkUserImportTargetAccount);
    const importSettings = useSelector(selectBulkUserImportImportSettings);
    const [uploadFileError, setUploadFileError] = useState<string>('');
    const {userFieldChange} = useEventTracking();

    useEffect(() => {
        if (accountId) {
            apiDispatch(getAccountName({requestBody: [accountId]})).then(response => {
                if (response.payload.success) {
                    dispatch(setTargetAccount({
                        accountId,
                        accountName: response.payload.data[accountId],
                    }));
                }
            });
        }
    }, [accountId, dispatch, apiDispatch]);

    const setTemplates = useCallback((data: boolean) => {
        dispatch(setImportSettings({
            ...importSettings,
            replaceLayouts: data,
        }));
    }, [importSettings, dispatch]);

    const setEmailAlerts = useCallback((data: boolean) => {
        dispatch(setImportSettings({
            ...importSettings,
            replaceAlerts: data,
        }));
    }, [importSettings, dispatch]);

    const setSavedSearches = useCallback((data: boolean) => {
        dispatch(setImportSettings({
            ...importSettings,
            replaceSearches: data,
        }));
    }, [importSettings, dispatch]);

    const setSelectedTemplateUserId = useCallback((id: number | null) => {
        dispatch(setImportSettings({
            ...importSettings,
            templateUserId: id,
        }));
    }, [importSettings, dispatch]);

    return (
        <div className="pd-l">
            <Group direction="v" gap="xxl" alignItems="start">
                {targetAccount ? (
                    <div>
                        <Label><RequiredMark label="Conference"/></Label>
                        <AccountLink
                            accountId={targetAccount.accountId}
                            accountName={targetAccount.accountName}
                            place="Bulk User Creation"
                            blank={true}
                        />
                    </div>
                ) : null}
                <UploadBulkUserImportFileBlock {...{
                    uploadFileError,
                    setUploadFileError,
                    conferenceName: targetAccount?.accountName}}
                />
                <div style={{width: 700}}>
                    <ImportSettings
                        key="ImportOptions"
                        {...{
                            selectedTemplateUserId: importSettings.templateUserId || null,
                            savedSearches: importSettings.replaceSearches,
                            emailAlerts: importSettings.replaceAlerts,
                            templates: importSettings.replaceLayouts,
                            setSelectedTemplateUserId,
                            setEmailAlerts,
                            setTemplates,
                            setSavedSearches,
                            onFieldChange: () => {
                                userFieldChange({
                                    section: 'Upload File',
                                    parameterName: 'Import options',
                                    viewType: 'Bulk Import Users',
                                });
                            },
                        }}
                    />
                    <ReplaceConfirmMessage {...{
                        disabled: isDisabled(importSettings),
                        templates: importSettings.replaceLayouts,
                        emailAlerts: importSettings.replaceAlerts,
                        savedSearches: importSettings.replaceSearches,
                        buttonName: 'Import',
                    }}
                    />
                </div>
            </Group>
        </div>
    );
};
